<template>
  <footer class="BaseFooter">
    <nav class="top" :aria-label="$pgettext('base footer nav', 'Site map')">
      <div class="container">
        <ul class="main-menu-ul row">
          <li
            v-for="(item, index) in menuOrFixture"
            :class="['main-menu-li col-12', index === menuOrFixture.length - 1 ? 'col-lg-6' : 'col-md-4 col-lg-3', {opened: (isMobile || ($currentStep === 'md' && index === menuOrFixture.length - 1)) && opened === index}]"
          >
            <h4 v-if="isMobile || ($currentStep === 'md' && index === menuOrFixture.length - 1)" class="menu-title menu-title-mobile">
              <a
                href="#"
                @click.prevent="opened = (opened === index) ? false : index"
                class="menu-title-link"
                role="button"
                :aria-label="item.label"
                :aria-controls="`footer-menu-${index}`"
                :aria-expanded="isMobile || ($currentStep === 'md' && index === menuOrFixture.length - 1) ? opened === index ? 'true' : 'false' : 'true'"
              >
                {{item.label}}
                <icon name="angle-down"></icon>
              </a>
            </h4>
            <h4 v-else class="menu-title">
              <a v-if="item.link" :href="item.link" :target="item.external ? '_blank' : false" :rel="item.external ? 'noopener' : false" class="menu-title-link">{{item.label}}</a>
              <template v-else>{{item.label}}</template>
            </h4>
            <ul
              :id="`footer-menu-${index}`"
              v-if="item.children"
              v-expand="isMobile || ($currentStep === 'md' && index === menuOrFixture.length - 1) ? opened === index : true"
              class="menu"
              :inert="(isMobile || ($currentStep === 'md' && index === menuOrFixture.length - 1) ? opened === index : true) ? null :  'true'"
            >
              <li v-if="item.link && (isMobile || ($currentStep === 'md' && index === menuOrFixture.length - 1))" class="menu-item">
                <a :href="item.link" :target="item.external ? '_blank' : false" :rel="item.external ? 'noopener' : false" class="menu-item-link">
                  <span v-if="item.external" class="with-icon">{{item.label}}<icon name="arrow-top-right"></icon></span>
                  <template v-else>{{item.label}}</template>
                </a>
              </li>
              <template v-for="subitem in item.children">
                <li class="menu-item">
                  <a
                    v-if="subitem.link"
                    :href="subitem.link"
                    :target="subitem.external ? '_blank' : false"
                    :rel="subitem.external ? 'noopener' : false"
                    class="menu-item-link"
                  >
                    <span v-if="subitem.external" class="with-icon">
                      {{subitem.label}}<icon name="arrow-top-right"></icon>
                    </span>
                    <template v-else>{{subitem.label}}</template>
                  </a>
                  <template v-else>{{subitem.label}}</template>
                </li>
                <li v-if="subitem.is_cookie" class="menu-item">
                  <button id="" class="menu-item-link ot-sdk-show-settings">Cookie Settings</button>
                </li>
              </template>
            </ul>
          </li>
        </ul>
      </div>
    </nav>
    <div class="bottom" aria-label="Company info">
      <div :class="$currentStep === 'xl' ? 'container-fluid p-0' : 'container'">
        <p class="bottom-text">
          <slot name="text"></slot>
        </p>
        <div class="row align-items-center">
          <div class="col-auto pt-2 pb-2">
            <h4 class="logo"><span class="hidden">Barilla</span></h4>
          </div>
          <portal class="d-none d-md-flex col-md pt-2 pb-2 align-items-md-center justify-content-md-start" :disabled="!isMobile" to="copyright">
            <p class="copyright-text">
              <slot name="copyright"></slot>
            </p>
          </portal>
          <div class="col col-md-3 col-lg-2 pt-2 pb-2">
            <socials :list="socials" ul-classes="d-flex align-items-center justify-content-end" position="footer"></socials>
          </div>
          <portal-target class="col-12 pt-2 d-md-none" name="copyright"></portal-target>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
  import Languages from '@components/languages'
  import Icon from '@components/atoms/icon'
  import Socials from "@components/socials"

  export default {
    name: 'BaseFooter',
    components: {Socials, Icon, Languages},
    data () {
      return {
        opened: false,
      }
    },
    props: {
      menu: {type: Array},
      socials: {type: Array},
      languages: {type: Array},
    },
    asyncComputed: {
      async menuOrFixture () {
        return this.menu ? this.menu :
          (await import('@fixtures/footer-fixture')).footerMenuFixture
      },
    },
    computed: {
      isMobile() {
        return this.$viewportWidth < 768
      },
    },
    mounted() {
      const unwatch = this.$watch(() => this.menuOrFixture, (newVal, oldVal) => {
        if (newVal !== oldVal) {
          this.opened = newVal.length - 1;
          if (unwatch) {
            unwatch();
          }
        }
      });
    },
    watch: {
      $currentStep: {
        handler(val) {
          if (val === 'md') {
            this.opened = this.menuOrFixture ? this.menuOrFixture.length - 1 : false;
          } else {
            this.opened = false;
          }
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "~$scss/variables";

  .BaseFooter {
    color: $color-white;
  }

  .top {
    background: fade-out($color-dark, 0.05);
    padding: 40px 0;

    @media (min-width: $bootstrap-lg) {
      padding: 80px 0;
    }
  }

  .bottom {
    background: $color-dark;
    padding-top: 40px;
    padding-bottom: 40px;

    @media (min-width: $bootstrap-xl) {
      padding-left: 80px;
      padding-right: 80px;
    }
  }

  .menu-title {
    font-size: 16px;
    line-height: 19.1px;
    text-transform: uppercase;
    font-weight: $bold;

    .menu-title-link {
      display: block;
      color: $color-white;
      transition: color .3s ease;

      &:hover {
        color: darken($color-grey-light, 10%);
      }
    }

    @media (min-width: $bootstrap-md) {
      position: relative;
      font-size: 18px;
      line-height: 21.49px;
      padding-bottom: 16px;

      &::after {
        content: '';
        display: block;
        height: 1px;
        background: rgba($color-white, .5);
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
      }
    }

    @media (min-width: $bootstrap-lg) {
      font-size: 20px;
      line-height: 23.88px;
    }
  }

  .menu-title-mobile {
    padding-bottom: 13px;

    @media (min-width: $bootstrap-md) {
      padding-bottom: 19px;
    }

    .menu-title-link {
      position: relative;
      display: block;
      padding-right: 30px;

      .icon {
        position: absolute;
        right: 0;
        top: 50%;
        margin-top: -12px;
        font-size: 24px;
        color: $color-white;
        transition: transform .3s ease;
      }
    }
  }

  .menu {
    padding: 0;
    line-height: 1.2;

    @media (min-width: $bootstrap-md) {
      padding: 24px 0 0;
    }

    @media (min-width: $bootstrap-lg) {
      padding: 40px 0 0;
    }

    .menu-item {
      padding-bottom: 16px;

      @media (max-width: $bootstrap-md - 1px) {
        &:first-child {
          padding-top: 7px;
        }

        &:last-child {
          padding-bottom: 20px;
        }
      }

      @media (min-width: $bootstrap-md) {
        &:last-child {
          padding-bottom: 0;
        }
      }

      @media (min-width: $bootstrap-lg) {
        padding-bottom: 24px;
      }

      .menu-item-link {
        display: block;
        font-size: 16px;
        font-weight: $normal;
        transition: color .3s ease;
        color: $color-white;
        text-decoration: underline;

        &:hover {
          color: darken($color-grey-light, 10%);
        }

        .with-icon {
          display: inline-block;
          max-width: 100%;
          padding-right: 31px;
          position: relative;
          text-decoration: underline;

          .icon {
            font-size: 16px;
            position: absolute;
            right: 0;
            top: 50%;
            margin-top: -8px;
          }
        }

        &.ot-sdk-show-settings{
          color: $color-white !important;
          border: 0 !important;
          height: auto !important;
          font-size: 16px !important;
          font-weight: $light !important;
          white-space: normal !important;
          word-wrap: break-word !important;
          padding: 0 !important;
          line-height: inherit !important;
          cursor: pointer !important;
          transition: color .3s ease !important;
          background:transparent !important;
          &:hover{
            background:transparent !important;
            color:$color-white !important;
          }
        }
      }
    }
  }

  .main-menu-ul {
    row-gap: 32px;

    @media (min-width: $bootstrap-md) {
      margin-left: -32px;
      margin-right: -32px;
    }

    @media (min-width: $bootstrap-lg) {
      margin-left: -50px;
      margin-right: -50px;
      row-gap: 56px;
    }

    .main-menu-li {
      @media (max-width: $bootstrap-md - 1) {
        position: relative;

        &::after {
          content: '';
          display: block;
          height: 1px;
          background: rgba($color-white, .5);
          position: absolute;
          left: 12px;
          right: 12px;
          bottom: 0;
        }
      }

      @media (min-width: $bootstrap-md) {
        padding-left: 32px;
        padding-right: 32px;
      }

      @media (min-width: $bootstrap-lg) {
        padding-left: 50px;
        padding-right: 50px;
      }

      @media (min-width: $bootstrap-md) {
        &:last-child {
          .menu {
            display: flex;
            flex-wrap: wrap;

            .menu-item {
              width: 33.333333%;

              &:not(:nth-child(3n)) {
                padding-right: 16px;
              }
            }
          }
        }
      }

      @media (min-width: $bootstrap-lg) {
        &:last-child {
          .menu {
            .menu-item {
              width: 50%;

              &:nth-child(odd) {
                padding-right: 24px !important;
              }

              &:nth-child(even) {
                padding-right: 0 !important;
              }
            }
          }
        }
      }
    }
  }

  .opened {
    .menu-title-mobile {
      .menu-title-link {
        color: $color-white;

        .icon {
          transform: rotate(180deg);
        }
      }
    }
  }

  .bottom-text {
    margin-bottom: 0;
    padding-bottom: 24px;
    font-size: 13px;
    color: rgba($color-white, 0.75);

    @media (min-width: $bootstrap-md) {
      font-size: 14px;
    }
  }

  .logo {
    width: 97px;
    height: 35px;
    background: url(~@images/barilla.svg) no-repeat 50% 50%;
    background-size: 100% 100%;
  }

  .copyright-text {
    font-size: 13px;

    @media (min-width: $bootstrap-md) {
      font-size: 14px;
    }
  }
</style>
